import { useQuery } from '@tanstack/react-query';
import { getWorkingUnitLocationMetadata, getWorkingUnitLocations } from '../../services/workingUnitLocation';
import { QueryKey } from '../../enums/QueryKey';
import { WorkingUnitLocation, WorkingUnitLocationMetadata } from '../../types/WorkingUnitLocation.interface';

export function useWorkingUnitLocations({
    location,
    hasMatchingOfferedCluster = false,
    dateFrom,
    dateTo,
    includeLatestDataMonth = false,
    includeCorrectedTotalsInfo = false,
    releasedOnly = false,
    fetchAll = false,
}: {
    location?: number;
    hasMatchingOfferedCluster?: boolean;
    dateFrom?: string;
    dateTo?: string;
    includeLatestDataMonth?: boolean;
    includeCorrectedTotalsInfo?: boolean;
    releasedOnly?: boolean;
    fetchAll?: boolean;
} = {}): [WorkingUnitLocation[], boolean, boolean] {
    const { data, isLoading, isError } = useQuery<WorkingUnitLocation[]>({
        queryKey: [
            QueryKey.WorkingUnitLocations,
            {
                location,
                hasMatchingOfferedCluster,
                dateFrom,
                dateTo,
                includeLatestDataMonth,
                includeCorrectedTotalsInfo,
                releasedOnly,
                fetchAll,
            },
        ],
        queryFn: () =>
            getWorkingUnitLocations({
                location,
                hasMatchingOfferedCluster,
                dateFrom,
                dateTo,
                includeLatestDataMonth,
                includeCorrectedTotalsInfo,
                releasedOnly,
            }),
        enabled: fetchAll || !!location,
        refetchOnWindowFocus: false,
    });

    return [data || [], isLoading, isError];
}

// Async selector enrichment: per-WUL latest-data / corrected-totals "through" months. Heavy
// aggregation, so it's cached generously (months change ~monthly) and kept off the base list query
// so it never blocks the dropdown — options render immediately, captions fill in when this resolves.
export function useWorkingUnitLocationMetadata({
    location,
    hasMatchingOfferedCluster = false,
    releasedOnly = false,
    enabled = true,
}: {
    location?: number;
    hasMatchingOfferedCluster?: boolean;
    releasedOnly?: boolean;
    enabled?: boolean;
} = {}): [WorkingUnitLocationMetadata[], boolean] {
    const { data, isLoading } = useQuery<WorkingUnitLocationMetadata[]>({
        queryKey: [QueryKey.WorkingUnitLocationMetadata, { location, hasMatchingOfferedCluster, releasedOnly }],
        queryFn: () => getWorkingUnitLocationMetadata({ location, hasMatchingOfferedCluster, releasedOnly }),
        enabled: enabled && !!location,
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000,
    });

    return [data || [], isLoading];
}
